import { useCallback } from "react";
import type { DropzoneOptions, FileRejection } from "react-dropzone";
import { ErrorCode, useDropzone } from "react-dropzone";

import { m } from "../paraglide/messages";
import { uiToast } from "../toast";

interface UseFileDropzoneOptions extends Omit<DropzoneOptions, "onDrop"> {
  onFilesAccepted: (files: File[]) => void;
  fileTypeLabel?: string;
}

export function useFileDropzone({
  onFilesAccepted,
  fileTypeLabel = "file",
  ...dropzoneOptions
}: UseFileDropzoneOptions) {
  const handleDrop = useCallback(
    (acceptedFiles: File[], rejections: FileRejection[]) => {
      const tooManyFiles = rejections.filter((r) =>
        r.errors.some((e) => e.code === ErrorCode.TooManyFiles),
      );

      const tooLargeFiles = rejections.filter((r) =>
        r.errors.some((e) => e.code === ErrorCode.FileTooLarge),
      );

      const invalidFiles = rejections.filter((r) =>
        r.errors.some((e) => e.code === ErrorCode.FileInvalidType),
      );

      const customErrors = rejections.filter((r) =>
        r.errors.some(
          (e) =>
            e.code !== ErrorCode.TooManyFiles &&
            e.code !== ErrorCode.FileTooLarge &&
            e.code !== ErrorCode.FileInvalidType,
        ),
      );

      if (customErrors.length > 0) {
        const messages = customErrors.flatMap((r) =>
          r.errors.map((e) => `${r.file.name}: ${e.message}`),
        );
        uiToast({
          title: messages.join(", "),
          severity: "danger",
        });
      }

      if (tooLargeFiles.length > 0) {
        uiToast({
          title: m.toast_file_too_large({
            files: tooLargeFiles.map((r) => r.file.name).join(", "),
          }),
          severity: "danger",
        });
      }

      if (invalidFiles.length > 0) {
        uiToast({
          title: `Invalid file type: ${invalidFiles.map((r) => r.file.name).join(", ")}`,
          description: `Only ${fileTypeLabel} files are supported`,
          severity: "danger",
        });
      }

      if (tooManyFiles.length > 0) {
        uiToast({
          title: m.toast_too_many_files_selected(),
          severity: "danger",
        });
      }

      if (
        tooLargeFiles.length > 0 ||
        invalidFiles.length > 0 ||
        tooManyFiles.length > 0 ||
        customErrors.length > 0
      ) {
        return;
      }

      if (acceptedFiles.length > 0) {
        onFilesAccepted(acceptedFiles);
      }
    },
    [onFilesAccepted, fileTypeLabel],
  );

  return useDropzone({
    ...dropzoneOptions,
    onDrop: handleDrop,
  });
}
